import React, { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Space, TableProps, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useStateCleanup } from '@hooks/useStateCleanup';
import { ALL_LABEL, COMMA_SEPARATOR, EDIT_BUTTON_LABEL, NEVER_LABEL } from '@constants/common';
import { StatusCodes, UserRoles, UserRolesMap } from '@constants/enum/common';
import { ICreatedBy } from '@redux/types/commonTypes';
import UserAvatar from '@components/common/userAvatar/userAvatar';
import DataTable from '@components/dataTable/DataTable';
import EmptyPageDescription from '@components/emptyPageDescription/EmptyPageDescription';
import TopNavigation from '@components/settings/TopNavigation';
import ApiKeyName from '@modules/APIKeys/components/ApiKeyName';
import {
  API_KEY_CREATE_BUTTON_LABEL,
  API_KEY_DEFAULT_FILTER,
  API_KEY_DEFAULT_MASK,
  API_KEY_FILTERS,
  API_KEY_REVOCATION,
  API_KEY_SORTING,
  API_KEY_TABLE_COLUMN_LABELS,
  API_KEY_TABLE_DATE_FORMAT,
  API_KEYS_DEFAULT_PAGE_SIZE,
  API_KEYS_HEADER,
  API_KEYS_SUBHEADING,
  EMPTY_API_KEYS_LIST_SECTION,
} from '@modules/APIKeys/constants/apiKeys';
import useApiKeys from '@modules/APIKeys/hooks/useApiKeys';
import { IApiKey } from '@modules/APIKeys/types/apiKeys';
import styles from './apiKeyList.module.scss';
import ApiKeyAddForm from '../apiKeyAddForm/ApiKeyAddForm';
import ApiKeyDeleteConfirmation from '../apiKeyDeleteConfirmation/ApiKeyDeleteConfirmation';
import ApiKeyEditForm from '../apiKeyEditForm/ApiKeyEditForm';
import ApiKeyViewer from '../apiKeyViewer/ApiKeyViewer';

const ApiKeyList: React.FC = () => {
  const {
    apiKey,
    apiKeys,
    apiToken,
    createApiKeyStatus,
    fetchApiKeys,
    fetchApiKeysStatus,
    getApikeys,
    hasActiveFilters,
    setApiKey,
    updateApiKeyStatus,
    deleteApiKeyStatus,
  } = useApiKeys();

  const { reset } = useStateCleanup();
  const [isApiKeyViewerOpen, setIsApiKeyViewerOpen] = useState<boolean>(false);
  const [isApiKeyAddFormOpen, setIsApiKeyAddFormOpen] = useState<boolean>(false);
  const [isApiKeyEditFormOpen, setIsApiKeyEditFormOpen] = useState<boolean>(false);
  const [isApiKeyDeleteConfirmationOpen, setIsApiKeyDeleteConfirmationOpen] =
    useState<boolean>(false);

  const isEmptyState =
    apiKeys.total === 0 && !(hasActiveFilters() || fetchApiKeysStatus === StatusCodes.IN_PROGRESS);

  const shouldRefetchData =
    createApiKeyStatus === StatusCodes.SUCCESS ||
    deleteApiKeyStatus === StatusCodes.SUCCESS ||
    updateApiKeyStatus === StatusCodes.SUCCESS;

  const columns: TableProps<IApiKey>['columns'] = [
    {
      title: API_KEY_TABLE_COLUMN_LABELS.NAME,
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (_: unknown, record: IApiKey) => <ApiKeyName apiKey={record} />,
    },
    {
      title: API_KEY_TABLE_COLUMN_LABELS.KEY,
      dataIndex: 'masked_token',
      key: 'masked_token',
      width: '12%',
      render: (mask: string) => mask || API_KEY_DEFAULT_MASK,
    },
    {
      title: API_KEY_TABLE_COLUMN_LABELS.EXPIRES,
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: '12%',
      render: (_: unknown, { expires, expires_at: expiresAt }: IApiKey) =>
        expires ? dayjs(expiresAt as string).format(API_KEY_TABLE_DATE_FORMAT) : NEVER_LABEL,
    },
    {
      title: API_KEY_TABLE_COLUMN_LABELS.WORKSPACES,
      dataIndex: 'workspaces',
      key: 'workspaces',
      render(_: unknown, { workspaces, all_workspaces: allWorkspaces }: IApiKey) {
        const text = allWorkspaces
          ? ALL_LABEL
          : workspaces.map(({ name }) => name).join(COMMA_SEPARATOR);
        return <div className={styles.wrapText}>{text}</div>;
      },
    },
    {
      title: API_KEY_TABLE_COLUMN_LABELS.ROLE,
      dataIndex: 'role',
      key: 'role',
      width: '12%',
      render: (role: UserRoles) => UserRolesMap[role],
    },
    {
      title: API_KEY_TABLE_COLUMN_LABELS.CREATED_BY,
      dataIndex: 'user',
      key: 'user',
      width: '10%',
      render: (user: Pick<ICreatedBy, 'given_name' | 'family_name'>) => (
        <UserAvatar user={{ user_id: '', ...user }} />
      ),
    },
    {
      key: 'action',
      width: '8%',
      render: (_: unknown, record: IApiKey) => (
        <Space size="small">
          <Tooltip title={EDIT_BUTTON_LABEL} placement="left">
            <Button
              className={styles.editButton}
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setApiKey(record);
                setIsApiKeyEditFormOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title={API_KEY_REVOCATION.BUTTON_LABEL} placement="left">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setApiKey(record);
                setIsApiKeyDeleteConfirmationOpen(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const closeAddForm = (showApiKey?: boolean) => {
    setIsApiKeyAddFormOpen(false);
    setIsApiKeyViewerOpen(showApiKey || false);
  };

  const closeApiKeyViewer = () => {
    setIsApiKeyViewerOpen(false);
    reset(['apiKeysStore.apiToken']);
  };

  useEffect(() => {
    if (fetchApiKeysStatus !== StatusCodes.IN_PROGRESS) {
      fetchApiKeys({ limit: API_KEYS_DEFAULT_PAGE_SIZE });
    }
  }, []);

  useEffect(() => {
    if (!shouldRefetchData) return;

    if (isEmptyState) {
      fetchApiKeys({ limit: API_KEYS_DEFAULT_PAGE_SIZE });
    }

    setTimeout(() => {
      reset([
        'apiKeysStore.createApiKeyStatus',
        'apiKeysStore.deleteApiKeyStatus',
        'apiKeysStore.updateApiKeyStatus',
      ]);
    });
  }, [shouldRefetchData, fetchApiKeys]);

  const renderEmptyState = () => {
    return (
      <EmptyPageDescription
        testId="emptyIndexesList"
        title={EMPTY_API_KEYS_LIST_SECTION.TITLE}
        description={EMPTY_API_KEYS_LIST_SECTION.DESCRIPTION}
        image={<div className={styles.emptyImage} />}
        action={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsApiKeyAddFormOpen(true)}
          >
            {API_KEY_CREATE_BUTTON_LABEL}
          </Button>
        }
      />
    );
  };

  const renderApiKeysList = () => (
    <>
      <Flex vertical>
        <h5 className={styles.header}>{API_KEYS_HEADER}</h5>
        <p>{API_KEYS_SUBHEADING}</p>
      </Flex>
      <section>
        <DataTable
          data={apiKeys.data}
          total={apiKeys.total}
          loading={fetchApiKeysStatus === StatusCodes.IN_PROGRESS}
          columns={columns}
          filters={API_KEY_FILTERS}
          selectedFiltersValues={API_KEY_DEFAULT_FILTER}
          getData={getApikeys}
          sorting={{
            allowClear: true,
            options: API_KEY_SORTING,
          }}
          refetch={shouldRefetchData}
          rowKey="api_token_id"
          pagination={{
            cursorPagination: true,
            pageSize: API_KEYS_DEFAULT_PAGE_SIZE,
          }}
          searchAvailable
          rowSelectionAvailable={false}
          primaryAction={{
            icon: <PlusOutlined />,
            label: API_KEY_CREATE_BUTTON_LABEL,
            onClick: () => setIsApiKeyAddFormOpen(true),
          }}
        />
      </section>
    </>
  );

  return (
    <div className="content-wrapper">
      <TopNavigation />
      <div className={styles.container}>
        {isEmptyState ? renderEmptyState() : renderApiKeysList()}
      </div>
      {isApiKeyAddFormOpen && <ApiKeyAddForm isOpen={isApiKeyAddFormOpen} onClose={closeAddForm} />}
      {isApiKeyViewerOpen && apiToken && (
        <ApiKeyViewer
          isOpen={isApiKeyViewerOpen}
          onClose={closeApiKeyViewer}
          unMaskedApiKey={apiToken}
        />
      )}
      {isApiKeyEditFormOpen && (
        <ApiKeyEditForm
          apiKey={apiKey}
          isOpen={isApiKeyEditFormOpen}
          onClose={() => setIsApiKeyEditFormOpen(false)}
          loading={updateApiKeyStatus === StatusCodes.IN_PROGRESS}
        />
      )}
      {isApiKeyDeleteConfirmationOpen && (
        <ApiKeyDeleteConfirmation
          apiKey={apiKey}
          isOpen={isApiKeyDeleteConfirmationOpen}
          onClose={() => setIsApiKeyDeleteConfirmationOpen(false)}
          loading={deleteApiKeyStatus === StatusCodes.IN_PROGRESS}
        />
      )}
    </div>
  );
};

export default ApiKeyList;
